/**
 * 器具・種目のカタログ。
 * 種目ごとに測り方 (kind) を持たせて、入力欄を切り替えられるようにしている。
 */
import type { EntryKind } from './types';

export type ExerciseDef = { name: string; kind: EntryKind };

export type Category = {
  id: string;
  label: string;
  /** 一覧での見分け用の短いラベル */
  short: string;
  items: ExerciseDef[];
};

const w = (name: string): ExerciseDef => ({ name, kind: 'weight' });
const r = (name: string): ExerciseDef => ({ name, kind: 'reps' });
const h = (name: string): ExerciseDef => ({ name, kind: 'hold' });
const c = (name: string): ExerciseDef => ({ name, kind: 'cardio' });

export const CATEGORIES: Category[] = [
  {
    id: 'chest',
    label: '胸',
    short: '胸',
    items: [
      w('ベンチプレス'),
      w('インクラインベンチプレス'),
      w('デクラインベンチプレス'),
      w('ダンベルプレス'),
      w('ダンベルフライ'),
      w('チェストプレス'),
      w('ペックフライ'),
      w('ケーブルクロスオーバー'),
      r('プッシュアップ'),
    ],
  },
  {
    id: 'back',
    label: '背中',
    short: '背',
    items: [
      w('ラットプルダウン'),
      w('シーテッドロー'),
      w('デッドリフト'),
      w('ベントオーバーロー'),
      w('ワンハンドダンベルロー'),
      w('プルオーバー'),
      w('バックエクステンション'),
      r('チンニング'),
      r('ラットプルダウン(アシスト)'),
    ],
  },
  {
    id: 'legs',
    label: '脚',
    short: '脚',
    items: [
      w('スクワット'),
      w('レッグプレス'),
      w('レッグエクステンション'),
      w('レッグカール'),
      w('カーフレイズ'),
      w('ヒップアブダクション'),
      w('ヒップアダクション'),
      w('ヒップスラスト'),
      w('ランジ'),
      r('自重スクワット'),
    ],
  },
  {
    id: 'shoulder',
    label: '肩',
    short: '肩',
    items: [
      w('ショルダープレス'),
      w('サイドレイズ'),
      w('フロントレイズ'),
      w('リアレイズ'),
      w('シュラッグ'),
      w('アップライトロー'),
    ],
  },
  {
    id: 'arms',
    label: '腕',
    short: '腕',
    items: [
      w('アームカール'),
      w('ハンマーカール'),
      w('プリーチャーカール'),
      w('トライセプスプレスダウン'),
      w('フレンチプレス'),
      w('キックバック'),
      w('リストカール'),
      r('ディップス'),
    ],
  },
  {
    id: 'core',
    label: '体幹',
    short: '幹',
    items: [
      w('アブドミナルクランチ'),
      w('ケーブルクランチ'),
      w('トーソローテーション'),
      r('クランチ'),
      r('レッグレイズ'),
      r('バイシクルクランチ'),
      h('プランク'),
      h('サイドプランク'),
      h('ホロウホールド'),
    ],
  },
  {
    id: 'cardio',
    label: '有酸素',
    short: '有',
    items: [
      c('トレッドミル'),
      c('ランニング'),
      c('ウォーキング'),
      c('エアロバイク'),
      c('クロストレーナー'),
      c('ローイングマシン'),
      c('ステアクライマー'),
      c('水泳'),
      c('縄跳び'),
    ],
  },
];

/** 種目名 → 測り方。カタログに無い名前 (手入力) は重量種目として扱う。 */
const KIND_BY_NAME = new Map<string, EntryKind>(
  CATEGORIES.flatMap((cat) => cat.items.map((item) => [item.name, item.kind] as const)),
);

export const kindOf = (name: string): EntryKind => KIND_BY_NAME.get(name.trim()) ?? 'weight';

export const ALL_EXERCISE_NAMES = CATEGORIES.flatMap((cat) => cat.items.map((i) => i.name));

/** その種目が属するカテゴリのラベル (見つからなければ null) */
export function categoryOf(name: string): string | null {
  const target = name.trim();
  for (const cat of CATEGORIES) {
    if (cat.items.some((i) => i.name === target)) return cat.label;
  }
  return null;
}
